import { useState } from 'react';
import { Badge, Card, Callout, DataTable, EmptyState, Field, Select, Skeleton } from '@api-hub/ui';
import { PageHeader } from '../components/Layout';
import { useResource } from '../lib/hooks';
import { formatCost, formatDate, formatNumber } from '../lib/format';

interface UsageRow {
  key: string;
  costStatus: string;
  requests: number;
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
  estimatedCostUsd: number;
}

interface DayRow {
  day: string;
  requests: number;
  tokens: number;
  estimatedCostUsd: number;
}

export function UsagePage() {
  const [groupBy, setGroupBy] = useState<'model' | 'providerAccount' | 'day'>('model');
  const { data, loading } = useResource<{ groupBy: string; items: UsageRow[] | DayRow[] }>(
    `/api/admin/usage?groupBy=${groupBy}`,
  );
  const prices = useResource<{
    items: Array<{
      id: string;
      providerType: string;
      model: string;
      inputPricePerMillion: string | null;
      outputPricePerMillion: string | null;
      effectiveFrom: string;
      effectiveTo: string | null;
      note: string | null;
    }>;
  }>('/api/admin/price-rules');

  return (
    <>
      <PageHeader
        title="Zużycie i koszty"
        description="Agregaty zużycia tokenów oraz szacowany koszt. Brak dopasowanej reguły cenowej oznacza koszt nieznany — nigdy zmyślony."
      />

      <Card
        title="Agregaty"
        actions={
          <Field label="Grupuj według">
            {(id) => (
              <Select
                id={id}
                value={groupBy}
                onChange={(event) => setGroupBy(event.target.value as typeof groupBy)}
              >
                <option value="model">Model</option>
                <option value="providerAccount">Konto dostawcy</option>
                <option value="day">Dzień</option>
              </Select>
            )}
          </Field>
        }
        tight
      >
        {loading ? (
          <div className="ah-card-body">
            <Skeleton rows={5} />
          </div>
        ) : groupBy === 'day' ? (
          <DataTable
            rows={(data?.items ?? []) as DayRow[]}
            rowKey={(row) => String(row.day)}
            empty={<EmptyState title="Brak zarejestrowanego zużycia" />}
            columns={[
              { key: 'day', header: 'Dzień', render: (row) => formatDate(row.day) },
              {
                key: 'requests',
                header: 'Żądania',
                align: 'right',
                render: (row) => formatNumber(row.requests),
              },
              {
                key: 'tokens',
                header: 'Tokeny',
                align: 'right',
                render: (row) => formatNumber(row.tokens),
              },
              {
                key: 'cost',
                header: 'Koszt',
                align: 'right',
                render: (row) => formatCost(row.estimatedCostUsd, 'KNOWN'),
              },
            ]}
          />
        ) : (
          <DataTable
            rows={(data?.items ?? []) as UsageRow[]}
            rowKey={(row) => `${row.key}:${row.costStatus}`}
            empty={<EmptyState title="Brak zarejestrowanego zużycia" />}
            columns={[
              {
                key: 'key',
                header: groupBy === 'model' ? 'Model' : 'Konto',
                render: (row) => <span className="ah-mono">{row.key}</span>,
              },
              {
                key: 'costStatus',
                header: 'Wycena',
                render: (row) => (
                  <Badge
                    tone={
                      row.costStatus === 'KNOWN'
                        ? 'ok'
                        : row.costStatus === 'PARTIAL'
                          ? 'warn'
                          : 'neutral'
                    }
                  >
                    {row.costStatus === 'KNOWN'
                      ? 'znana'
                      : row.costStatus === 'PARTIAL'
                        ? 'częściowa'
                        : 'nieznana'}
                  </Badge>
                ),
              },
              {
                key: 'requests',
                header: 'Żądania',
                align: 'right',
                render: (row) => formatNumber(row.requests),
              },
              {
                key: 'input',
                header: 'Tokeny we.',
                align: 'right',
                render: (row) => formatNumber(row.inputTokens),
              },
              {
                key: 'output',
                header: 'Tokeny wy.',
                align: 'right',
                render: (row) => formatNumber(row.outputTokens),
              },
              {
                key: 'cost',
                header: 'Koszt',
                align: 'right',
                render: (row) => formatCost(row.estimatedCostUsd, row.costStatus),
              },
            ]}
          />
        )}
      </Card>

      <Card
        title="Reguły cenowe"
        description="Ceny są edytowalne i datowane. Zweryfikuj je wobec aktualnego cennika dostawcy — nie są stałą w kodzie."
        tight
      >
        <Callout tone="warn">
          Reguły z seeda mają charakter startowy. Zanim oprzesz na nich rozliczenia, potwierdź
          stawki u dostawcy i dodaj nową regułę z odpowiednią datą obowiązywania.
        </Callout>
        <DataTable
          rows={prices.data?.items ?? []}
          rowKey={(row) => row.id}
          empty={
            <EmptyState
              title="Brak reguł cenowych"
              description="Bez reguł koszt każdego wywołania pozostanie nieznany."
            />
          }
          columns={[
            { key: 'provider', header: 'Dostawca', render: (row) => row.providerType },
            {
              key: 'model',
              header: 'Model',
              render: (row) => <span className="ah-mono">{row.model}</span>,
            },
            {
              key: 'input',
              header: 'Wejście / 1M',
              align: 'right',
              render: (row) => (row.inputPricePerMillion ? `${row.inputPricePerMillion} USD` : '—'),
            },
            {
              key: 'output',
              header: 'Wyjście / 1M',
              align: 'right',
              render: (row) =>
                row.outputPricePerMillion ? `${row.outputPricePerMillion} USD` : '—',
            },
            {
              key: 'effective',
              header: 'Obowiązuje',
              render: (row) =>
                `${formatDate(row.effectiveFrom)} → ${row.effectiveTo ? formatDate(row.effectiveTo) : 'nadal'}`,
            },
          ]}
        />
      </Card>
    </>
  );
}
